import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Play,
  Mic,
  FileText,
  Brain,
  Shield,
  Clock,
  CheckCircle,
  Sparkles,
  Activity,
  Lock,
  HeartPulse,
  Stethoscope,
  Eye,
  Users,
  Image
} from 'lucide-react';
import { LandingLayout } from '../components/LandingLayout';
import {
  AnimatedSection,
  StaggerContainer,
  StaggerItem,
  ScaleIn
} from '../components/AnimatedSection';
import { useLanguage } from '../i18n';
import { TrustBar, BetaProgram } from '../components/BetaProgram';

export const HomePage = () => {
  const { t, dir } = useLanguage();

  const features = [
    {
      icon: Mic,
      title: t.features.voice.title,
      description: t.features.voice.description,
      color: 'from-forest to-forest-400'
    },
    {
      icon: Brain,
      title: t.features.ai.title,
      description: t.features.ai.description,
      color: 'from-bark-700 to-bark-500'
    },
    {
      icon: FileText,
      title: t.features.soap.title,
      description: t.features.soap.description,
      color: 'from-forest-400 to-accent'
    },
    {
      icon: Shield,
      title: t.features.security.title,
      description: t.features.security.description,
      color: 'from-bark-500 to-bark-300'
    },
  ];

  const specialties = [
    { name: t.solutions.generalPractice, icon: Stethoscope },
    { name: t.solutions.internalMedicine, icon: Activity },
    { name: t.solutions.pediatrics, icon: Users },
    { name: t.solutions.cardiology, icon: HeartPulse },
    { name: t.solutions.dermatology, icon: Eye },
    { name: t.solutions.orthopedics, icon: Users },
    { name: t.solutions.psychiatry, icon: Brain },
    { name: t.solutions.radiology, icon: Image },
  ];

  return (
    <LandingLayout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden" dir={dir}>
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-forest/5 via-transparent to-transparent" />
        <div className="absolute top-20 end-0 w-[600px] h-[600px] bg-forest/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 start-0 w-[400px] h-[400px] bg-accent/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest/10 text-forest text-sm font-medium mb-6"
              >
                <Sparkles className="w-4 h-4" />
                <span>{t.hero.badge}</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-bark-700 leading-tight mb-6"
              >
                {t.hero.title}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                  {t.hero.titleHighlight}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-xl text-bark-700/60 mb-8 max-w-lg"
              >
                {t.hero.description}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-wrap gap-4"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-bark-700 text-white font-semibold hover:bg-bark-600 transition-all shadow-lg shadow-bark-700/25 hover:shadow-bark-700/40 hover:-translate-y-0.5"
                >
                  {t.hero.startTrial}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="#demo"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-sand-300/30 text-bark-700 font-semibold hover:bg-sand-300/50 transition-all"
                >
                  <Play className="w-5 h-5" />
                  {t.hero.watchDemo}
                </a>
              </motion.div>

            </div>

            {/* Right Content - Product Preview */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative hidden lg:block"
            >
              <div className="relative bg-gradient-to-br from-bark-700 to-bark-600 rounded-2xl p-1 shadow-2xl">
                <div className="bg-bark-700 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-bark-600">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500" />
                      <div className="w-3 h-3 rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-bark-500 rounded-lg px-4 py-1.5 text-sand-400 text-sm">
                        app.medkit.ai
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-semibold">Patient Consultation</h3>
                      <span className="px-2 py-1 bg-accent/20 text-accent text-xs rounded-full flex items-center gap-1">
                        <span className="w-2 h-2 bg-accent rounded-full animate-pulse" />
                        Recording
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-bark-600 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 rounded-full bg-forest flex items-center justify-center flex-shrink-0">
                            <Mic className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sand-300 text-sm">
                              "Patient presents with persistent cough for two weeks, no fever, mild fatigue..."
                            </p>
                            <div className="mt-2 flex gap-2">
                              <span className="px-2 py-0.5 bg-forest/20 text-forest-300 text-xs rounded">Chief Complaint</span>
                              <span className="px-2 py-0.5 bg-accent/20 text-accent text-xs rounded">HPI</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-forest/20 to-accent/20 rounded-lg p-4 border border-forest/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Brain className="w-5 h-5 text-forest-300" />
                          <span className="text-forest-300 text-sm font-medium">AI Suggestions</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sand-300 text-sm">
                            <CheckCircle className="w-4 h-4 text-accent" />
                            <span>Consider chest X-ray to rule out pneumonia</span>
                          </div>
                          <div className="flex items-center gap-2 text-sand-300 text-sm">
                            <CheckCircle className="w-4 h-4 text-accent" />
                            <span>Document smoking history</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="absolute -start-8 top-1/4 bg-sand-50 rounded-xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <p className="text-bark-700 font-semibold">15 min saved</p>
                    <p className="text-bark-700/60 text-sm">per consultation</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute -end-4 bottom-1/4 bg-sand-50 rounded-xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-forest/10 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-forest" />
                  </div>
                  <div>
                    <p className="text-bark-700 font-semibold">SOAP Notes</p>
                    <p className="text-bark-700/60 text-sm">generated instantly</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <TrustBar dir={dir} />

      {/* Why MedKit - Pain Point Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-sand-300/30 to-sand-50" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-red-50 text-red-600 text-sm font-medium mb-4">
              {t.whyMedkit.badge}
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-bark-700 mb-4">
              {t.whyMedkit.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-400">
                {t.whyMedkit.titleHighlight}
              </span>
            </h2>
            <p className="text-xl text-bark-700/60 max-w-2xl mx-auto">
              {t.whyMedkit.description}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              { title: t.whyMedkit.points.burnout, desc: t.whyMedkit.points.burnoutDesc, icon: HeartPulse },
              { title: t.whyMedkit.points.errors, desc: t.whyMedkit.points.errorsDesc, icon: FileText },
              { title: t.whyMedkit.points.focus, desc: t.whyMedkit.points.focusDesc, icon: Eye },
              { title: t.whyMedkit.points.fast, desc: t.whyMedkit.points.fastDesc, icon: Mic },
            ].map((point, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="flex gap-5 bg-sand-50 rounded-2xl p-8 border border-sand-300 hover:border-forest/20 transition-all hover:shadow-lg">
                  <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center flex-shrink-0">
                    <point.icon className="w-6 h-6 text-forest" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-bark-700 mb-2">{point.title}</h3>
                    <p className="text-bark-700/60">{point.desc}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-sand-50 to-sand-300/30" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-forest/10 text-forest text-sm font-medium mb-4">
              Features
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-bark-700 mb-4">
              {t.features.title}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                {t.features.titleHighlight}
              </span>
            </h2>
            <p className="text-xl text-bark-700/60 max-w-2xl mx-auto">
              {t.features.description}
            </p>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div className="group bg-sand-50 rounded-2xl p-8 shadow-lg shadow-bark-700/5 hover:shadow-xl transition-all duration-300 border border-sand-300 hover:border-forest/20">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-bark-700 mb-3">{feature.title}</h3>
                  <p className="text-bark-700/60 leading-relaxed">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-forest/10 text-forest text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-bark-700 mb-4">
              {t.howItWorks.title}
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                {t.howItWorks.titleHighlight}
              </span>
            </h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: t.howItWorks.step1.title, description: t.howItWorks.step1.description, icon: Mic },
              { step: '02', title: t.howItWorks.step2.title, description: t.howItWorks.step2.description, icon: Brain },
              { step: '03', title: t.howItWorks.step3.title, description: t.howItWorks.step3.description, icon: FileText }
            ].map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.15}>
                <div className="relative">
                  <div className="text-8xl font-bold text-sand-300/50 absolute -top-4 -start-2">
                    {item.step}
                  </div>
                  <div className="relative bg-sand-50 rounded-2xl p-8 border border-sand-300 hover:border-forest/30 transition-colors">
                    <div className="w-12 h-12 rounded-xl bg-forest/10 flex items-center justify-center mb-6">
                      <item.icon className="w-6 h-6 text-forest" />
                    </div>
                    <h3 className="text-xl font-bold text-bark-700 mb-3">{item.title}</h3>
                    <p className="text-bark-700/60">{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 lg:py-28 bg-sand-300/30" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-forest/10 text-forest text-sm font-medium mb-4">
              {t.specialties.badge}
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-bark-700 mb-4">
              {t.specialties.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest to-forest-400">
                {t.specialties.titleHighlight}
              </span>
            </h2>
            <p className="text-xl text-bark-700/60 max-w-2xl mx-auto">
              {t.specialties.description}
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {specialties.map((specialty, index) => (
              <StaggerItem key={index}>
                <div className="group bg-sand-50 rounded-2xl p-6 border border-sand-300 hover:border-forest/30 hover:shadow-lg transition-all text-center">
                  <div className="w-14 h-14 rounded-xl bg-forest/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-forest/20 transition-colors">
                    <specialty.icon className="w-7 h-7 text-forest" />
                  </div>
                  <h3 className="font-semibold text-bark-700">{specialty.name}</h3>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Compliance & Security Section */}
      <section className="py-20 lg:py-28 bg-bark-700" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-forest/20 text-forest-300 text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              {t.compliance.badge}
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
              {t.compliance.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-forest-300 to-accent">
                {t.compliance.titleHighlight}
              </span>
            </h2>
            <p className="text-xl text-sand-400 max-w-2xl mx-auto">
              {t.compliance.description}
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Shield, title: t.compliance.hipaa, desc: t.compliance.hipaaDesc },
              { icon: Shield, title: t.compliance.gdpr, desc: t.compliance.gdprDesc },
              { icon: Lock, title: t.compliance.encryption, desc: t.compliance.encryptionDesc },
              { icon: Eye, title: t.compliance.privacy, desc: t.compliance.privacyDesc },
            ].map((item, index) => (
              <StaggerItem key={index}>
                <div className="bg-bark-600 rounded-2xl p-6 border border-bark-500">
                  <div className="w-12 h-12 rounded-xl bg-forest/20 flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-forest-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-sand-400 text-sm">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Beta Program (demo embed, perks & signup) */}
      <BetaProgram dir={dir} />

      {/* CTA Section */}
      <section className="py-20 lg:py-28" dir={dir}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <ScaleIn>
            <div className="relative bg-gradient-to-br from-bark-700 to-bark-600 rounded-3xl p-12 lg:p-20 overflow-hidden">
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 end-0 w-96 h-96 bg-white rounded-full blur-3xl" />
                <div className="absolute bottom-0 start-0 w-64 h-64 bg-white rounded-full blur-3xl" />
              </div>

              <div className="relative text-center">
                <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
                  {t.cta.title}
                </h2>
                <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                  {t.cta.description}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-accent text-bark-700 font-semibold hover:bg-accent/90 transition-all shadow-lg hover:-translate-y-0.5"
                  >
                    {t.cta.startTrial}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-white/20 text-white font-semibold hover:bg-white/30 transition-all backdrop-blur-sm"
                  >
                    {t.cta.talkToSales}
                  </Link>
                </div>
                <p className="mt-6 text-white/70 text-sm">
                  {t.cta.noCreditCard}
                </p>
              </div>
            </div>
          </ScaleIn>
        </div>
      </section>
    </LandingLayout>
  );
};
